type LoaderInput<T> = {
    /**
     * The default value to use before loading
     */
    defaultValue: T;

    /**
     * The function that loads the data
     *
     * This should return the data that the LoaderWithReset will hold
     */
    loaderFunction: () => Promise<T>;

    /**
     * Optional promise to wait for before loading, so we can wait for any dependencies to the loader to load first
     */
    waitOnBeforeLoad?: Promise<void>;

    /**
     * Optional function to post-process the loaded data before storing it, NB! You can still mutate the data after loading, this is just for initial processing
     */
    postProcessFunction?: (data: T) => T;
};
export class LoaderWithReset<T> {
    private currentlyLoading: Promise<void> | null = null;
    private waitable: Promise<void> | null = null;
    private loader: () => Promise<T>;

    private postProcessFunction: ((data: T) => T) | null = null;

    private defaultValue: T;

    public Value!: T;

    private setValue(value: T) {
        if (this.postProcessFunction) {
            this.Value = this.postProcessFunction(value);
        } else {
            this.Value = value;
        }
    }

    /**
     * Create a new loader with reset
     */
    constructor(input: LoaderInput<T>) {
        const { defaultValue, loaderFunction: loader, waitOnBeforeLoad: waitable } = input;

        this.waitable = waitable ?? null;
        this.loader = loader;
        this.defaultValue = defaultValue;
        this.postProcessFunction = input.postProcessFunction ?? null;
        this.setValue(defaultValue);
    }

    public async Load(): Promise<void> {
        await this.waitable;

        // are we already loaded?
        if (this.currentlyLoading == null) {
            // loader
            this.currentlyLoading = new Promise<void>((resolve, reject) => {
                this.loader()
                    .then((res) => {
                        this.setValue(res);
                        resolve();
                    })
                    .catch((e) => {
                        console.error(e);
                        reject(e);
                    })
                    .finally(() => {});
            });
        }

        // wait for load
        await this.currentlyLoading;
    }

    public Reset(): void {
        this.setValue(this.defaultValue);
        this.currentlyLoading = null;
    }

    public async ResetAndReload() {
        if (this.currentlyLoading == null) {
            // we return here as we have not loaded yet so noone uses the data
            return;
        }
        this.Reset();
        this.Load();
    }

    /**
     * It is better if we reload data instead of reset and reload because the moment we set the Value to null or default value, it will trigger everyone that
     * watches the data, and then it will trigger again when it is finished loading. But if we reload the data, it will only trigger once when it is finished loading and changes the value.
     * @returns
     */
    public async Reload() {
        if (this.currentlyLoading == null) {
            return;
        }
        this.currentlyLoading = null;
        await this.Load();
    }
}
